import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Users, UserCheck, Trophy, UserX } from 'lucide-react';

const DEFAULTS = {
  active_supplier_threshold_pct: 50,
  min_bids_per_tender: 1,
  key_supplier_win_pct: 60,
  new_supplier_months: 3,
  inactivity_unassign_months: 6,
  reassign_block_months: 12,
  auto_invite_by_product: true,
  block_publish_unassigned: true,
};

function SettingsSection({ isSuperAdmin }) {
  const [values, setValues] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/metrics-settings')
      .then(({ data }) => setValues(v => ({ ...v, ...(data || {}) })))
      .catch(() => {});
  }, []);

  const numField = (k, label) => (
    <div className="space-y-1">
      <label className="text-xs text-slate-600">{label}</label>
      <Input type="number" step="0.1" value={values[k] ?? ''}
        onChange={e => setValues(p => ({ ...p, [k]: e.target.value === '' ? '' : Number(e.target.value) }))}
        disabled={!isSuperAdmin}
        data-testid={`metrics-setting-${k}`}
        className="h-8 text-xs rounded-sm border-slate-300" />
    </div>
  );

  const boolField = (k, label) => (
    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
      <input type="checkbox" checked={!!values[k]}
        onChange={e => setValues(p => ({ ...p, [k]: e.target.checked }))}
        disabled={!isSuperAdmin}
        data-testid={`metrics-setting-${k}`}
        className="rounded border-slate-300" />
      <span>{label}</span>
    </label>
  );

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/admin/metrics-settings', values);
      toast.success('Настройки сохранены');
    } catch (e) {
      toast.error((e.response?.data?.detail) || 'Не удалось сохранить');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4" data-testid="metrics-settings">
      <div className="text-sm font-heading font-bold text-slate-900">Настройки метрик</div>
      <div className="grid md:grid-cols-3 gap-3">
        {numField('active_supplier_threshold_pct', 'Порог активного участника, %')}
        {numField('min_bids_per_tender', 'Мин. ставок на тендер')}
        {numField('key_supplier_win_pct', 'Порог ключевого участника (побед %)')}
        {numField('new_supplier_months', 'Окно «новый поставщик», мес.')}
        {numField('inactivity_unassign_months', 'Открепление после неактивности, мес.')}
        {numField('reassign_block_months', 'Блок обратного закрепления, мес.')}
      </div>
      <div className="flex flex-wrap gap-4">
        {boolField('auto_invite_by_product', 'Автодобавление участников по продукту')}
        {boolField('block_publish_unassigned', 'Блокировать публикацию при не распределённых участниках')}
      </div>
      {isSuperAdmin && (
        <div>
          <Button onClick={save} disabled={loading}
            data-testid="metrics-settings-save"
            className="h-8 px-4 rounded-sm bg-[#0046AD] hover:bg-[#003A8C] text-white text-xs">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Сохранить
          </Button>
        </div>
      )}
      {!isSuperAdmin && (
        <p className="text-[11px] text-slate-500">Редактирование доступно только супер-администратору.</p>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#0046AD]" />}
        <div className="text-sm font-heading font-bold text-slate-900">{title}</div>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

function FilterBar({ value, onChange }) {
  const years = [];
  const nowY = new Date().getFullYear();
  for (let y = nowY - 3; y <= nowY + 1; y++) years.push(y);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get('/products').then(r => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="metrics-filters">
      <div className="space-y-1">
        <label className="text-[11px] text-slate-600 block">Год</label>
        <Select value={value.year} onValueChange={v => onChange({ ...value, year: v })}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-sm" data-testid="filter-year">
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] text-slate-600 block">Квартал</label>
        <Select value={value.quarter} onValueChange={v => onChange({ ...value, quarter: v })}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-sm" data-testid="filter-quarter">
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            {[1, 2, 3, 4].map(q => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 flex-1 min-w-[16rem]">
        <label className="text-[11px] text-slate-600 block">Продукт</label>
        <Select value={value.product} onValueChange={v => onChange({ ...value, product: v })}>
          <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="filter-product">
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            {products.map(p => (
              <SelectItem key={p.id || p.name} value={p.name}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function buildQuery(filters) {
  const p = new URLSearchParams();
  if (filters.year && filters.year !== 'all') p.set('year', filters.year);
  if (filters.quarter && filters.quarter !== 'all') p.set('quarter', filters.quarter);
  if (filters.product && filters.product !== 'all') p.set('product', filters.product);
  const s = p.toString();
  return s ? `?${s}` : '';
}

function ActiveSuppliersBlock({ filters }) {
  const [data, setData] = useState({ rows: [], threshold_pct: 50 });
  const q = useMemo(() => buildQuery(filters), [filters]);
  useEffect(() => { api.get(`/admin/archive/analytics/active-suppliers${q}`).then(r => setData(r.data || { rows: [] })).catch(() => {}); }, [q]);
  return (
    <Section title={`Активные участники (по кварталам, > ${data.threshold_pct}% участий) — группировка по закреплённому организатору`} icon={Users} testid="metrics-active-suppliers">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs">Квартал</TableHead>
            <TableHead className="text-xs">Поставщик</TableHead>
            <TableHead className="text-xs">Ответственный</TableHead>
            <TableHead className="text-xs">Организатор</TableHead>
            <TableHead className="text-xs text-right">Участий (matched/invited)</TableHead>
            <TableHead className="text-xs text-right">%</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data.rows || []).map((r, i) => (
            <TableRow key={i} data-testid={`active-row-${i}`}>
              <TableCell className="text-xs">{r.year} Q{r.quarter}</TableCell>
              <TableCell className="text-xs font-medium">{r.company_name}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.responsible || '—'}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.organizer_name || '—'}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.matched_tenders}/{r.invited_tenders}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.share_pct}%</TableCell>
            </TableRow>
          ))}
          {(!data.rows || data.rows.length === 0) && (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 text-xs py-4">Пока нет активных участников</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

function NewActiveSuppliersBlock({ filters }) {
  const [data, setData] = useState({ rows: [] });
  const q = useMemo(() => buildQuery(filters), [filters]);
  useEffect(() => { api.get(`/admin/archive/analytics/new-active-suppliers${q}`).then(r => setData(r.data || { rows: [] })).catch(() => {}); }, [q]);
  return (
    <Section title={`Новые активные участники (последние ${data.new_supplier_months || 3} мес.)`} icon={UserCheck} testid="metrics-new-suppliers">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs">Квартал</TableHead>
            <TableHead className="text-xs">Поставщик</TableHead>
            <TableHead className="text-xs">Ответственный</TableHead>
            <TableHead className="text-xs text-right">Участий (matched/invited)</TableHead>
            <TableHead className="text-xs text-right">%</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data.rows || []).map((r, i) => (
            <TableRow key={i} data-testid={`new-active-row-${i}`}>
              <TableCell className="text-xs">{r.year} Q{r.quarter}</TableCell>
              <TableCell className="text-xs font-medium">{r.company_name}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.responsible || '—'}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.matched_tenders}/{r.invited_tenders}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.share_pct}%</TableCell>
            </TableRow>
          ))}
          {(!data.rows || data.rows.length === 0) && (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-400 text-xs py-4">Нет данных</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

function KeySuppliersBlock({ filters }) {
  const [data, setData] = useState({ rows: [], win_threshold_pct: 60 });
  const q = useMemo(() => buildQuery(filters), [filters]);
  useEffect(() => { api.get(`/admin/archive/analytics/key-suppliers${q}`).then(r => setData(r.data || { rows: [] })).catch(() => {}); }, [q]);
  return (
    <Section title={`Ключевые участники (побед > ${data.win_threshold_pct}% в квартале по продукту)`} icon={Trophy} testid="metrics-key-suppliers">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs">Квартал</TableHead>
            <TableHead className="text-xs">Продукт</TableHead>
            <TableHead className="text-xs">Победитель</TableHead>
            <TableHead className="text-xs">Ответственный</TableHead>
            <TableHead className="text-xs text-right">Побед</TableHead>
            <TableHead className="text-xs text-right">Выигранный объём</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data.rows || []).map((r, i) => (
            <TableRow key={i} data-testid={`key-supplier-row-${i}`}>
              <TableCell className="text-xs">{r.year} Q{r.quarter}</TableCell>
              <TableCell className="text-xs">{r.product_name}</TableCell>
              <TableCell className="text-xs font-medium">{r.company_name}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.responsible || '—'}</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.wins}/{r.tenders_total} ({r.win_pct}%)</TableCell>
              <TableCell className="text-xs text-right tabular-nums">{r.won_volume}</TableCell>
            </TableRow>
          ))}
          {(!data.rows || data.rows.length === 0) && (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 text-xs py-4">Нет данных</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

function UnassignedBlock() {
  const [data, setData] = useState({ rows: [] });
  useEffect(() => { api.get('/admin/archive/analytics/unassigned-suppliers').then(r => setData(r.data || { rows: [] })).catch(() => {}); }, []);
  return (
    <Section title="Не распределённые поставщики" icon={UserX} testid="metrics-unassigned">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs">Компания</TableHead>
            <TableHead className="text-xs">Ответственный</TableHead>
            <TableHead className="text-xs">Email</TableHead>
            <TableHead className="text-xs">Прежний организатор</TableHead>
            <TableHead className="text-xs">Открепление</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data.rows || []).filter(r => !r.stop_list).map((r, i) => (
            <TableRow key={i} data-testid={`unassigned-row-${i}`}>
              <TableCell className="text-xs font-medium">{r.company_name}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.responsible || '—'}</TableCell>
              <TableCell className="text-xs text-slate-600">{r.email}</TableCell>
              <TableCell className="text-xs">{r.previous_organizer_name || '—'}</TableCell>
              <TableCell className="text-xs text-slate-500">{r.unassigned_at ? new Date(r.unassigned_at).toLocaleDateString('ru-RU') : '—'}</TableCell>
            </TableRow>
          ))}
          {(!data.rows || data.rows.length === 0) && (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-400 text-xs py-4">Все поставщики распределены</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

export default function MetricsTab({ isSuperAdmin }) {
  const [filters, setFilters] = useState({ year: 'all', quarter: 'all', product: 'all' });
  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <SettingsSection isSuperAdmin={isSuperAdmin} />
      <div className="bg-white border border-slate-200 rounded-sm p-3">
        <FilterBar value={filters} onChange={setFilters} />
      </div>
      <ActiveSuppliersBlock filters={filters} />
      <NewActiveSuppliersBlock filters={filters} />
      <KeySuppliersBlock filters={filters} />
      <UnassignedBlock />
    </div>
  );
}
